import * as React from 'react';
import {Slate, Editable, withReact, useSlate, ReactEditor} from 'slate-react';
import {createEditor, Transforms, Editor} from 'slate';
import {withHistory} from 'slate-history';
import {
  emotionStyled as styled,
  styledDiv,
  createLegacyVars,
  tokens,
} from '@packages/utilities/styled';

import {hasErrors} from '@packages/systems/wfdl';
import {BindingPill} from './bindingPill';
import {serializeInput} from './utils';
import {ContextualExpression} from '@packages/utilities/contextual-expression';
import type {Expression} from '@packages/systems/wfdl-ast';

export const BINDED_PILL = 'BINDED_PILL' as const;

export type SlateNode =
  | {
      text: string;
    }
  | {
      type: string;
      token: string;
      children: Array<SlateNode>;
    };
export type SlateState = Array<{
  children: Array<SlateNode>;
}>;

// @ts-expect-error - TS7006 - Parameter 'theme' implicitly has an 'any' type.
const lineHeight = (theme) =>
  theme.lineHeights.copy + theme.control.borderWidth * 2;
// @ts-expect-error - TS7006 - Parameter 'theme' implicitly has an 'any' type.
const verticalPadding = (theme) =>
  theme.control.height - theme.control.borderWidth * 2 - lineHeight(theme);

const {localTokens, useLegacyTheme} = createLegacyVars((theme) => ({
  lineHeight: lineHeight(theme),
  fontFamily: theme.typography.copy.fontFamily,
  borderWidth: theme.control.borderWidth,
  borderColor: tokens.colors.uiInputBorderColor,
  borderRadius: theme.control.borderRadius,
  backgroundColor: tokens.colors.uiInputBackground,
  boxShadow: tokens.boxShadows.inputInner,
  color: tokens.colors.textPrimary,
  verticalPadding: verticalPadding(theme) / 2,
  horizontalPadding: theme.gutters.x4,
  focus: {
    backgroundColor: tokens.colors.uiInputBackground,
    borderColor: tokens.colors.blueBorder,
  },
  multiLine: {
    minHeight: `${theme.control.height * 4}px`,
  },
  hasErrors: {
    borderColor: tokens.colors.redBorder,
    focus: {
      borderColor: tokens.colors.redBorder,
    },
  },
}));

const StyledSlateEditable = styled(Editable)(
  {
    fontFamily: localTokens.fontFamily,
    lineHeight: localTokens.lineHeight,
    borderWidth: localTokens.borderWidth,

    borderStyle: 'solid',
    borderColor: localTokens.borderColor,
    borderRadius: localTokens.borderRadius,
    backgroundColor: localTokens.backgroundColor,
    boxShadow: localTokens.boxShadow,
    color: localTokens.color,

    paddingTop: localTokens.verticalPadding,
    paddingBottom: localTokens.verticalPadding,
    paddingLeft: localTokens.horizontalPadding,
    paddingRight: localTokens.horizontalPadding,

    cursor: 'text',

    whiteSpace: 'pre-wrap',

    '&:focus': {
      backgroundColor: localTokens.focus.backgroundColor,
      borderColor: localTokens.focus.borderColor,
    },
    '& ::selection': {
      backgroundColor: '#2596ff',
    },
  },
  {
    variants: {
      multiLine: {
        true: {
          // This is needed, some other min-height style rule is overriding this one
          minHeight: localTokens.multiLine.minHeight,

          // IN CASE WE WANT TO ADD SCROLLBARS ~~>
          // ------------------------------------
          // overflowY: 'scroll !important',
          // overflowX: 'hidden',
          // '::-webkit-scrollbar': {
          //   width: 10,
          //   cursor: 'default',
          // },
          // '::-webkit-scrollbar-track': {
          //   // backgroundColor: theme.colors.backgroundDeep,
          //   backgroundClip: 'padding-box',
          // },
          //
          // '::-webkit-scrollbar-thumb': {
          //   backgroundColor: theme.colors.backgroundVivid,
          //   borderStyle: 'solid',
          //   borderColor: 'transparent',
          //   borderWidth: '0 0 0 1px',
          //   backgroundClip: 'content-box',
          //
          //   '&:hover': {
          //     backgroundColor: theme.colors.control,
          //   },
          // },
          // ------------------------------------
        },
      },
      hasErrors: {
        true: {
          borderColor: localTokens.hasErrors.borderColor,
          '&:focus': {
            borderColor: localTokens.hasErrors.focus.borderColor,
          },
        },
      },
    },
  }
);

const DefaultElementStyle = styledDiv(({theme}) => ({
  padding: 0,
  margin: `${theme.lineHeights.oneline * 2}px 0 0 0`,
  lineHeight: `${theme.lineHeights.copy}px`,
  overflowWrap: 'anywhere',
}));

/**
 * Renders the SlateJS context for the input editor. Props
 * _must_ be referentially equal across renders to avoid a bug
 * where focusing requires two clicks instead of one.
 */
export const SlateContext = React.memo<{
  children: React.ReactNode;
  multiLine: boolean;
  getInitialState: () => SlateState;
  onExpressionChange: (expr: Expression) => void;
}>(function SlateContext({
  children,
  multiLine,
  getInitialState,
  onExpressionChange,
}) {
  // Create a Slate editor object that won't change across renders.
  const editor = React.useMemo(
    () =>
      withLines(
        withBindedPills(withReact(withHistory(createEditor() as ReactEditor))),
        multiLine
      ),
    [multiLine]
  );
  const [value, setValue] = React.useState(getInitialState);
  const valueRef = React.useRef(value);
  valueRef.current = value;

  const onChange = React.useCallback(
    // @ts-expect-error - TS7006 - Parameter 'nextValue' implicitly has an 'any' type.
    (nextValue) => {
      setValue(nextValue);
      if (
        valueRef.current !== nextValue &&
        // Only update the editor if the value has actually changed.
        // @ts-expect-error - TS7006 - Parameter 'op' implicitly has an 'any' type.
        editor.operations.some((op) => 'set_selection' !== op.type)
      ) {
        onExpressionChange(serializeInput(editor));
      }
    },
    [editor, onExpressionChange]
  );

  return (
    <Slate editor={editor} value={value} onChange={onChange}>
      {children}
    </Slate>
  );
});

export const SlateTextUI = React.memo<{
  multiLine: boolean;
  placeholder: string;
  onChange: () => void;
  expr: ContextualExpression;
}>(function SlateTextUI({multiLine, placeholder, expr, onChange}) {
  const editor = useSlate();
  const renderElement = React.useCallback(
    // @ts-expect-error - TS7006 - Parameter 'props' implicitly has an 'any' type.
    (props) => {
      const {attributes, children, element} = props;
      switch (element.type) {
        case BINDED_PILL:
          return (
            <BindingPill
              element={element}
              attributes={attributes}
              token={element.token}
              expr={expr}
              onChange={onChange}
            >
              <div contentEditable={false}>{children}</div>
            </BindingPill>
          );
        default:
          return (
            <DefaultElementStyle {...attributes}>
              {children}
            </DefaultElementStyle>
          );
      }
    },
    [onChange, expr]
  );

  React.useEffect(
    // This below code only needs to run once
    // during initial render of the editor contents.
    // See https://github.com/ianstormtaylor/slate/issues/4000
    () => {
      Editor.normalize(editor, {force: true});
    },
    [editor]
  );

  const cssVars = useLegacyTheme();

  return (
    <StyledSlateEditable
      renderElement={renderElement}
      placeholder={placeholder}
      multiLine={multiLine}
      hasErrors={hasErrors(expr)}
      style={cssVars}
    />
  );
});

const withBindedPills = (editor: any) => {
  const {isInline, isVoid} = editor;

  editor.isInline = (element: any) => {
    return element.type === BINDED_PILL ? true : isInline(element);
  };

  editor.isVoid = (element: any) => {
    return element.type === BINDED_PILL ? true : isVoid(element);
  };

  return editor;
};

function withLines(editor: any, multiLine?: boolean) {
  const {normalizeNode} = editor;
  // When multi-line is not the implemented for the field, enforces 1-line input
  if (!multiLine) {
    editor.normalizeNode = ([node, path]: [any, any]) => {
      if (path.length === 0) {
        if (editor.children.length > 1) {
          Transforms.mergeNodes(editor);
        }
      }

      return normalizeNode([node, path]);
    };
  }

  return editor;
}
